import { apolloClient } from '../vue-apollo';
import vuetify from '../plugins/vuetify';
import { getTenantAssetBaseUrl } from './tenant';
const viewBrandSettingQuery = require('../settings/api/viewBrandSetting.graphql');

// Last successfully fetched settings, kept per tenant domain so the next page
// load can theme itself without waiting on the network.
const BRAND_CACHE_KEY = 'BRAND_SETTINGS_CACHE';

/**
 * Applies the locally cached brand settings, if any.
 * @returns {boolean} true when a cached copy existed and was applied
 */
export function applyCachedBrandSettings() {
  try {
    const raw = localStorage.getItem(BRAND_CACHE_KEY);
    if (!raw) return false;
    applyBrandSettings(JSON.parse(raw));
    return true;
  } catch (error) {
    console.warn('Failed to apply cached brand settings:', error);
    return false;
  }
}

/**
 * Loads brand settings from the server and applies them to the application
 */
export async function loadAndApplyBrandSettings() {
  try {
    // Ensure apolloClient is available
    if (!apolloClient || !apolloClient.query) {
      console.warn('Apollo client not available for brand settings');
      return null;
    }

    const result = await apolloClient.query({
      query: viewBrandSettingQuery,
      fetchPolicy: 'network-only', // Always fetch fresh data on app load
      errorPolicy: 'all' // Return partial data even if there are errors
    });

    const brandSettings = result.data?.viewBrandSetting;
    
    if (brandSettings) {
      applyBrandSettings(brandSettings);
      try {
        localStorage.setItem(BRAND_CACHE_KEY, JSON.stringify(brandSettings));
      } catch (storageError) {
        // Storage full or unavailable — caching is best-effort only
      }
      return brandSettings;
    } else {
      console.log('No brand settings found in response');
    }
    
    return null;
  } catch (error) {
    console.error('Failed to load brand settings:', error);
    // Don't throw - allow app to continue with defaults
    return null;
  }
}

/**
 * Applies brand settings to the DOM, Vuetify theme, and favicons
 */
export function applyBrandSettings(brandSettings) {
  const root = document.documentElement;
  
  // Apply sidebar colors to CSS variables and Vuetify theme
  if (brandSettings.sidebar) {
    const sidebar = brandSettings.sidebar;
    
    // Set CSS custom properties
    if (sidebar.backgroundColor) {
      root.style.setProperty('--theme-primary', sidebar.backgroundColor);
      root.style.setProperty('--sidebar-background-color', sidebar.backgroundColor);

      // Dynamically calculate and set theme contrast colors
      const contrast = getContrastColor(sidebar.backgroundColor);
      root.style.setProperty('--theme-primary-contrast', contrast.text);
      root.style.setProperty('--theme-primary-contrast-muted', contrast.muted);
      root.style.setProperty('--theme-primary-contrast-border', contrast.border);
      root.style.setProperty('--theme-primary-logout-bg', contrast.logoutBg);
      root.style.setProperty('--theme-primary-logout-text', contrast.logoutText);
    }
    if (sidebar.secondaryColor) {
      root.style.setProperty('--theme-secondary', sidebar.secondaryColor);
      root.style.setProperty('--sidebar-secondary-color', sidebar.secondaryColor);
    }
    if (sidebar.fontColor) {
      root.style.setProperty('--sidebar-font-color', sidebar.fontColor);
    }
    
    // Update Vuetify theme
    if (vuetify && vuetify.framework && vuetify.framework.theme) {
      if (sidebar.backgroundColor) {
        vuetify.framework.theme.themes.light.primary = sidebar.backgroundColor;
      }
      if (sidebar.secondaryColor) {
        vuetify.framework.theme.themes.light.secondary = sidebar.secondaryColor;
      }
    }
    
    // Apply sidebar typography
    if (sidebar.fontFamily) {
      root.style.setProperty('--sidebar-font-family', sidebar.fontFamily);
    }
    if (sidebar.fontSize) {
      root.style.setProperty('--sidebar-font-size', `${sidebar.fontSize}px`);
    }
    if (sidebar.fontWeight) {
      root.style.setProperty('--sidebar-font-weight', sidebar.fontWeight);
    }
    if (sidebar.fontStyle) {
      root.style.setProperty('--sidebar-font-style', sidebar.fontStyle);
    }
    if (sidebar.lineHeight) {
      root.style.setProperty('--sidebar-line-height', `${sidebar.lineHeight}px`);
    }
    if (sidebar.letterSpacing !== undefined) {
      root.style.setProperty('--sidebar-letter-spacing', `${sidebar.letterSpacing}px`);
    }
  }
  
  // Apply logos
  if (brandSettings.logo) {
    if (brandSettings.logo.horizontal) {
      root.style.setProperty('--logo-horizontal', `url(${getImageUrl(brandSettings.logo.horizontal)})`);
    }
    if (brandSettings.logo.vertical) {
      root.style.setProperty('--logo-vertical', `url(${getImageUrl(brandSettings.logo.vertical)})`);
    }
  }
  
  // Apply favicons
  if (brandSettings.favicon) {
    applyFavicons(brandSettings.favicon);
  }
  
  // Store in global variable for later use
  window.__brandSettings = brandSettings;
}

/**
 * Applies favicons to the document head
 */
function applyFavicons(favicon) {
  // Remove existing favicon links
  const existingLinks = document.querySelectorAll('link[rel*="icon"]');
  existingLinks.forEach(link => link.remove());
  
  // Helper to create favicon link
  const createFaviconLink = (href, sizes, rel = 'icon') => {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = getImageUrl(href);
    if (sizes) link.sizes = sizes;
    document.head.appendChild(link);
  };
  
  // Apply favicons in order of preference
  if (favicon.size16) createFaviconLink(favicon.size16, '16x16');
  if (favicon.size32) createFaviconLink(favicon.size32, '32x32');
  if (favicon.size150) createFaviconLink(favicon.size150, '150x150', 'apple-touch-icon');
  if (favicon.size180) createFaviconLink(favicon.size180, '180x180', 'apple-touch-icon');
  if (favicon.size192) createFaviconLink(favicon.size192, '192x192');
  if (favicon.size256) createFaviconLink(favicon.size256, '256x256');
  if (favicon.size512) createFaviconLink(favicon.size512, '512x512');
}

/**
 * Gets the full URL for an image path
 * If it's already a full URL, return as is
 * Otherwise, prepend the base URL
 */
function getImageUrl(path) {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // Build tenant-aware base URL from saved tenant id
  const baseUrl = getTenantAssetBaseUrl();
  return baseUrl ? `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}` : path;
}

/**
 * Gets the primary (horizontal) logo URL from brand settings
 * Falls back to default logo if brand settings are not available
 */
export function getPrimaryLogoUrl(defaultLogo = null) {
  const brandSettings = window.__brandSettings;
  if (brandSettings && brandSettings.logo && brandSettings.logo.horizontal) {
    return getImageUrl(brandSettings.logo.horizontal);
  }
  return defaultLogo;
}

/**
 * Gets the secondary (vertical) logo URL from brand settings
 * Falls back to default logo if brand settings are not available
 */
export function getSecondaryLogoUrl(defaultLogo = null) {
  const brandSettings = window.__brandSettings;
  if (brandSettings && brandSettings.logo && brandSettings.logo.vertical) {
    return getImageUrl(brandSettings.logo.vertical);
  }
  return defaultLogo;
}

/**
 * Calculates high-contrast text, muted, and border colors
 * based on a background hex color using W3C YIQ brightness standards.
 */
function getContrastColor(hexColor) {
  if (!hexColor) {
    return {
      text: '#ffffff',
      muted: 'rgba(255, 255, 255, 0.75)',
      border: 'rgba(255, 255, 255, 0.15)',
      logoutBg: 'rgba(239, 68, 68, 0.2)',
      logoutText: '#fca5a5'
    };
  }
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return {
      text: '#ffffff',
      muted: 'rgba(255, 255, 255, 0.75)',
      border: 'rgba(255, 255, 255, 0.15)',
      logoutBg: 'rgba(239, 68, 68, 0.2)',
      logoutText: '#fca5a5'
    };
  }
  // YIQ formula
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  const isLight = yiq >= 186;
  return isLight ? {
    text: '#1f2937', // dark slate
    muted: 'rgba(31, 41, 55, 0.65)',
    border: 'rgba(31, 41, 55, 0.15)',
    logoutBg: 'rgba(220, 38, 38, 0.1)',
    logoutText: '#b91c1c'
  } : {
    text: '#ffffff', // white
    muted: 'rgba(255, 255, 255, 0.75)',
    border: 'rgba(255, 255, 255, 0.15)',
    logoutBg: 'rgba(239, 68, 68, 0.2)',
    logoutText: '#fca5a5'
  };
}
